import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Plus, Upload, Trash2, ArrowLeft, KeyRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAlbum, setNewAlbum] = useState({ title: '', description: '' });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: '', next: '', confirm: '' });
  const [changingPwd, setChangingPwd] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // NOTE: localStorage auth is not production-ready (vulnerable to XSS)
  // TODO: Replace with httpOnly cookies or secure session management before production
  useEffect(() => {
    // Check if already authenticated
    const auth = localStorage.getItem('adminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    
    try {
      const response = await axios.post(`${API}/admin/auth/login`, { password });
      
      if (response.data.success) {
        localStorage.setItem('adminAuth', 'true');
        setIsAuthenticated(true);
        toast({
          title: 'Success!',
          description: 'Logged in successfully'
        });
        fetchAlbums();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Invalid password',
        variant: 'destructive'
      });
    } finally {
      setLoggingIn(false);
      setPassword('');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
    setSelectedAlbum(null);
    toast({
      title: 'Logged out',
      description: 'You have been logged out successfully'
    });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwdForm.next !== pwdForm.confirm) {
      toast({
        title: 'Error',
        description: 'New password and confirmation do not match',
        variant: 'destructive'
      });
      return;
    }
    if (pwdForm.next.length < 8) {
      toast({
        title: 'Error',
        description: 'New password must be at least 8 characters',
        variant: 'destructive'
      });
      return;
    }
    setChangingPwd(true);
    try {
      await axios.post(`${API}/admin/auth/change-password`, {
        current_password: pwdForm.current,
        new_password: pwdForm.next
      });
      toast({
        title: 'Success!',
        description: 'Password updated successfully'
      });
      setPwdForm({ current: '', next: '', confirm: '' });
      setShowPasswordForm(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update password',
        variant: 'destructive'
      });
    } finally {
      setChangingPwd(false);
    }
  };

  const fetchAlbums = async () => {
    try {
      const response = await axios.get(`${API}/albums`);
      setAlbums(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch albums',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchAlbums();
    }
  }, [isAuthenticated]); // fetchAlbums is defined above, toast is stable

  const createAlbum = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/albums`, newAlbum);
      toast({
        title: 'Success!',
        description: 'Album created successfully'
      });
      setNewAlbum({ title: '', description: '' });
      setShowCreateForm(false);
      fetchAlbums();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create album',
        variant: 'destructive'
      });
    }
  };

  const deleteAlbum = async (albumId) => {
    if (!window.confirm('Are you sure you want to delete this album?')) return;
    
    try {
      await axios.delete(`${API}/admin/albums/${albumId}`);
      toast({
        title: 'Success!',
        description: 'Album deleted'
      });
      fetchAlbums();
      setSelectedAlbum(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete album',
        variant: 'destructive'
      });
    }
  };

  const handlePhotoUpload = async (e, albumId) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploadingPhoto(true);
    try {
      await axios.post(`${API}/admin/albums/${albumId}/photos/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast({
        title: 'Success!',
        description: 'Photo uploaded successfully'
      });
      const response = await axios.get(`${API}/albums/${albumId}`);
      setSelectedAlbum(response.data);
      fetchAlbums();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to upload photo',
        variant: 'destructive'
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e, albumId) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    const file = files[0];
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      toast({
        title: 'Error',
        description: 'Please upload an image or video file',
        variant: 'destructive'
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploadingPhoto(true);
    try {
      await axios.post(`${API}/admin/albums/${albumId}/photos/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast({
        title: 'Success!',
        description: 'File uploaded successfully'
      });
      const response = await axios.get(`${API}/albums/${albumId}`);
      setSelectedAlbum(response.data);
      fetchAlbums();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to upload file',
        variant: 'destructive'
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const deletePhoto = async (albumId, photoIndex) => {
    if (!window.confirm('Delete this photo?')) return;
    
    try {
      await axios.delete(`${API}/admin/albums/${albumId}/photos/${photoIndex}`);
      toast({
        title: 'Success!',
        description: 'Photo deleted'
      });
      const response = await axios.get(`${API}/albums/${albumId}`);
      setSelectedAlbum(response.data);
      fetchAlbums();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete photo',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      {!isAuthenticated ? (
        // Login Screen
        <div className="max-w-md mx-auto mt-20">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-center">Admin Login</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter admin password"
                    required
                    className="w-full"
                  />
                </div>
                <Button type="submit" disabled={loggingIn} className="w-full bg-blue-600 hover:bg-blue-700">
                  {loggingIn ? 'Logging in...' : 'Login'}
                </Button>
              </form>
              <div className="mt-4 text-center">
                <Button onClick={() => navigate('/')} variant="link" className="text-gray-600">
                  Back to Portfolio
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        // Admin Dashboard (existing content)
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Album Manager</h1>
              <p className="text-gray-600 mt-2">Manage your photo albums</p>
            </div>
            <div className="flex gap-4 flex-wrap">
              <Button onClick={handleLogout} variant="outline" className="text-red-600 hover:text-red-700" data-testid="admin-logout-btn">
                Logout
              </Button>
              <Button onClick={() => navigate('/')} variant="outline" data-testid="admin-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Portfolio
              </Button>
              <Button onClick={() => setShowPasswordForm(!showPasswordForm)} variant="outline" data-testid="admin-change-password-btn">
                <KeyRound className="w-4 h-4 mr-2" />
                Change Password
              </Button>
              <Button onClick={() => setShowCreateForm(!showCreateForm)} className="bg-blue-600 hover:bg-blue-700" data-testid="admin-new-album-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Album
              </Button>
            </div>
          </div>

        {showPasswordForm && (
          <Card className="mb-8" data-testid="change-password-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="w-5 h-5" />
                Change Admin Password
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                  <Input
                    type="password"
                    value={pwdForm.current}
                    onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })}
                    placeholder="Enter current password"
                    required
                    data-testid="change-password-current-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                  <Input
                    type="password"
                    value={pwdForm.next}
                    onChange={(e) => setPwdForm({ ...pwdForm, next: e.target.value })}
                    placeholder="At least 8 characters"
                    minLength={8}
                    required
                    data-testid="change-password-new-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                  <Input
                    type="password"
                    value={pwdForm.confirm}
                    onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
                    placeholder="Re-enter new password"
                    minLength={8}
                    required
                    data-testid="change-password-confirm-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={changingPwd} className="bg-blue-600 hover:bg-blue-700" data-testid="change-password-submit-btn">
                    {changingPwd ? 'Updating...' : 'Update Password'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setPwdForm({ current: '', next: '', confirm: '' });
                    }}
                    data-testid="change-password-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {showCreateForm && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Create New Album</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={createAlbum} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Album Title</label>
                  <Input
                    value={newAlbum.title}
                    onChange={(e) => setNewAlbum({...newAlbum, title: e.target.value})}
                    placeholder="e.g., Science Fair 2026"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <Textarea
                    value={newAlbum.description}
                    onChange={(e) => setNewAlbum({...newAlbum, description: e.target.value})}
                    placeholder="Describe the album..."
                    rows={3}
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Create Album</Button>
                  <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {!selectedAlbum ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {albums.map((album) => (
              <Card key={album._id} className="cursor-pointer hover:shadow-xl transition-shadow" onClick={() => setSelectedAlbum(album)}>
                <CardHeader>
                  <CardTitle className="text-xl">{album.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  {album.cover_image && (
                    album.cover_image.startsWith('data:video/') ? (
                      <video
                        src={album.cover_image}
                        muted
                        playsInline
                        className="w-full h-48 object-cover rounded-lg mb-4 bg-black"
                      />
                    ) : (
                      <img src={album.cover_image} alt={album.title} className="w-full h-48 object-cover rounded-lg mb-4" />
                    )
                  )}
                  <p className="text-gray-600 text-sm mb-4">{album.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">{album.photos?.length || 0} photos</span>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteAlbum(album._id);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div>
            <Button onClick={() => setSelectedAlbum(null)} variant="outline" className="mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Albums
            </Button>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-2xl">{selectedAlbum.title}</CardTitle>
                <p className="text-gray-600">{selectedAlbum.description}</p>
              </CardHeader>
              <CardContent>
                {/* Drag and Drop Zone */}
                <div
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, selectedAlbum._id)}
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    uploadingPhoto 
                      ? 'border-gray-300 bg-gray-50' 
                      : 'border-blue-300 bg-blue-50 hover:border-blue-500 hover:bg-blue-100'
                  }`}
                >
                  <input
                    type="file"
                    accept="image/*,video/*"
                    id="photo-upload"
                    className="hidden"
                    onChange={(e) => handlePhotoUpload(e, selectedAlbum._id)}
                    disabled={uploadingPhoto}
                  />
                  <label 
                    htmlFor="photo-upload" 
                    className="cursor-pointer block"
                  >
                    <Upload className="w-12 h-12 mx-auto mb-4 text-blue-600" />
                    <p className="text-lg font-medium text-gray-700 mb-2">
                      {uploadingPhoto ? 'Uploading...' : 'Drag and drop photo or video here'}
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      or click to browse
                    </p>
                    <Button 
                      type="button"
                      disabled={uploadingPhoto} 
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById('photo-upload').click();
                      }}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingPhoto ? 'Uploading...' : 'Choose Photo/Video'}
                    </Button>
                  </label>
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {selectedAlbum.photos?.map((photo, index) => (
                <div key={index} className="relative group">
                  {photo.type === 'video' ? (
                    <video
                      src={photo.url}
                      controls
                      className="w-full h-48 object-cover rounded-lg bg-black"
                    />
                  ) : (
                    <img
                      src={photo.url}
                      alt={photo.caption}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  )}
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => deletePhoto(selectedAlbum._id, index)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  {photo.caption && (
                    <p className="text-sm text-gray-600 mt-2">{photo.caption}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        </div>
      )}
    </div>
  );
};

export default Admin;